import { useState, type ReactNode } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { errorMessage } from '@/api/errorMessage';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  /** Destructive actions get the error color. */
  destructive?: boolean;
  /** Asks for a free-text reason (e.g. suspension). */
  reasonLabel?: string;
  onConfirm(reason: string): Promise<unknown>;
  onClose(): void;
}

/** Confirmation for actions that are hard to undo (suspend, remove, revoke...). */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  destructive,
  reasonLabel,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);

  const close = () => {
    setError(null);
    onClose();
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      setReason('');
      onClose();
    } catch (caught) {
      // Stays open so the person sees why nothing happened and can retry.
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : close} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error !== null && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage(error, 'Não foi possível concluir. Tente de novo.')}
          </Alert>
        )}
        <DialogContentText>{description}</DialogContentText>
        {reasonLabel && (
          <TextField
            label={reasonLabel}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 2 }}
            slotProps={{ htmlInput: { maxLength: 300 } }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          onClick={() => void confirm()}
          disabled={busy}
        >
          {busy ? <CircularProgress size={20} color="inherit" /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
